import React, { useState, useEffect } from 'react';
import { fetchPresentationsList, Presentation, PresentationVersion, PresentationFile } from '../services/api';
import { PRESENTATIONS_API_BASE_URL } from '../config';

const Presentations: React.FC = () => {
  const [presentations, setPresentations] = useState<Presentation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPresentations = async () => {
      try {
        setLoading(true);
        const data = await fetchPresentationsList();
        setPresentations(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load presentations');
      } finally {
        setLoading(false);
      }
    };

    loadPresentations();
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getLatestVersion = (presentation: Presentation): PresentationVersion | null => {
    if (!presentation.versions || presentation.versions.length === 0) return null;
    return presentation.versions[presentation.versions.length - 1];
  };

  const getFilesForVersion = (version: PresentationVersion): PresentationFile[] => {
    if (version.files && version.files.length > 0) {
      return version.files;
    }

    if (version.filename) {
      return [
        {
          filename: version.filename,
          file_size: version.file_size || 0,
        },
      ];
    }

    return [];
  };

  const getDownloadUrl = (presentationId: string, versionId: string, filename?: string) => {
    const base = `${PRESENTATIONS_API_BASE_URL}/api/download/${presentationId}/${versionId}`;
    return filename ? `${base}/${encodeURIComponent(filename)}` : base;
  };

  const sortedPresentations = [...presentations].sort((a, b) => {
    const aKey = `${a.speaker_name || ''} ${a.presentation_title || ''}`.toLowerCase();
    const bKey = `${b.speaker_name || ''} ${b.presentation_title || ''}`.toLowerCase();
    if (aKey < bKey) return -1;
    if (aKey > bKey) return 1;
    return 0;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
        <div className="container mx-auto px-4 py-16">
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00529B]"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
        <div className="container mx-auto px-4 py-16">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-8" role="alert">
            <strong className="font-bold">Error:</strong>
            <span className="block sm:inline"> {error}</span>
          </div>
        </div>
      </div>
    );
  }

  const hasPresentations = sortedPresentations.length > 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-white to-gray-50 pt-16">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Presentations
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Download the presentations from the Language Intelligence Conference.
          </p>
        </div>

        {hasPresentations && (
          <div className="text-center mb-8">
            <span className="inline-block bg-[#00529B] text-white px-4 py-2 rounded-full text-sm font-medium">
              {sortedPresentations.length} Presentation{sortedPresentations.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}

        {hasPresentations ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {sortedPresentations.map((presentation) => {
              const latestVersion = getLatestVersion(presentation);
              if (!latestVersion) {
                return null;
              }

              const files = getFilesForVersion(latestVersion);

              return (
                <div
                  key={presentation.id}
                  className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between h-full"
                >
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">
                      {presentation.presentation_title}
                    </h3>
                    <p className="text-sm font-medium text-[#00529B] mb-1">
                      {presentation.speaker_name}
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                      Last updated {formatDate(presentation.updated_at)}
                    </p>
                  </div>

                  <div className="mt-2 space-y-2">
                    {files.map((file) => (
                      <a
                        key={file.filename}
                        href={getDownloadUrl(presentation.id, latestVersion.version_id, file.filename)}
                        className="inline-flex items-center justify-center w-full px-4 py-2 bg-[#00529B] text-white text-sm font-medium rounded-lg hover:bg-[#003D74] transition-colors duration-300"
                        download={file.filename}
                      >
                        <span className="truncate">
                          Download {file.filename} ({formatFileSize(file.file_size)})
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No presentations available yet</h3>
            <p className="text-gray-600 mb-6">
              Presentations will be published here after the conference.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Presentations;
